import { SummaryValue, type GenericFormatter } from "hooks/queries/types";
import type { CellContext } from "@tanstack/table-core";

export const handleNullishSummaryValue = (value: SummaryValue): string => {
  if (value === null) {
    return "NULL";
  }

  if (value === undefined) {
    return "N/A";
  }

  if (typeof value === "string" && value.trim() === "") {
    return "EMPTY";
  }

  return String(value);
};

export const truncFloat = new Intl.NumberFormat("en-US", {
  notation: "compact",
  compactDisplay: "short",
  roundingMode: "trunc",
  minimumIntegerDigits: 1,
  maximumFractionDigits: 1,
} satisfies Intl.NumberFormatOptions);

export const twoDecimalPlaceRounding = new Intl.NumberFormat("en-US", {
  roundingMode: "halfExpand",
  minimumIntegerDigits: 1,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} satisfies Intl.NumberFormatOptions);

export const twoDecimalPlaceRoundingCellFormatter = <TData>(
  props: CellContext<TData, number | null | undefined>,
) => {
  const value = props.getValue();
  return typeof value === "number" ? twoDecimalPlaceRounding.format(value) : "";
};

export const scienceNotationRounding = new Intl.NumberFormat("en-US", {
  notation: "scientific",
  roundingMode: "halfExpand",
  minimumIntegerDigits: 1,
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
} satisfies Intl.NumberFormatOptions);

export const threeDecimalPlaceRounding = new Intl.NumberFormat("en-US", {
  roundingMode: "halfExpand",
  minimumIntegerDigits: 1,
  minimumFractionDigits: 3,
  maximumFractionDigits: 3,
} satisfies Intl.NumberFormatOptions);

// #region formatter overrides
export const skipNumberFormat = {
  format: (value: number) => {
    return String(value);
  },
} as Intl.NumberFormat;

export const coerceNumberFormat = (
  formatter = skipNumberFormat,
  fallback = "N/A",
  predicate: (value: number) => boolean = Boolean,
) => {
  return {
    format: (value: number) => {
      if (predicate(value)) {
        return formatter.format(value);
      }

      return fallback;
    },
  } as Intl.NumberFormat;
};

export const falsyToNAFormatter: GenericFormatter = (value) => {
  if (value) {
    return String(value);
  }

  return "N/A";
};
// #endregion
export const threeDecimalPlaceRoundingCellFormatter = <TData>(
  props: CellContext<TData, number | null | undefined>,
) => {
  const value = props.getValue();
  return typeof value === "number"
    ? threeDecimalPlaceRounding.format(value)
    : "";
};

export const yearRoundingCellFormatter = <TData>(
  props: CellContext<TData, string | null | undefined>,
) => {
  const value = props.getValue();
  return typeof value === "string" ? value.split("-")[0] : "";
};

export const scienceNotationRoundingCellFormatter = <TData>(
  props: CellContext<TData, number | null | undefined>,
) => {
  const value = props.getValue();
  return typeof value === "number" ? scienceNotationRounding.format(value) : "";
};

export const nthNumberFormatter = (number: number) => {
  const remainder100 = Math.abs(number) % 100;
  if (remainder100 >= 11 && remainder100 <= 13) {
    return "th";
  }
  const remainder10 = Math.abs(number) % 10;
  switch (remainder10) {
    case 1:
      return "st";
    case 2:
      return "nd";
    case 3:
      return "rd";
    default:
      return "th";
  }
};
